import { IncomingMessage, ServerResponse } from 'http';
import { useMemo } from 'react';
import {
  HttpLink,
  ApolloLink,
  FetchResult,
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition, Observable } from '@apollo/client/utilities';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import get from 'lodash/get';
import omitDeep from 'omit-deep-lodash';
import { WITSBY_USER_EMAIL_COOKIE_NAME } from '@constants';
// import { handleGraphqlError } from '@utils';

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

let apolloClient: ApolloClient<NormalizedCacheObject>;
let isRefreshing = false;
let tokenRefreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null;
const failedRequestsQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
  operation: any;
  forward: any;
}> = [];

export type ResolverContext = {
  req?: IncomingMessage;
  res?: ServerResponse;
};

const logOut = () => {
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const isSafePath =
    currentPath.startsWith('/') && !currentPath.startsWith('//') && currentPath !== '/';
  if (isSafePath) {
    sessionStorage.setItem('witsby-post-login-redirect', currentPath);
  }
  deleteCookie('source');
  deleteCookie('witsby-role');
  deleteCookie('accessToken');
  deleteCookie('refreshToken');
  deleteCookie('magazineAccessed');
  deleteCookie('isELBadgeMember', { domain: '.ascd.org' });
  deleteCookie('isPaywallMember', { domain: '.ascd.org' });
  deleteCookie('isActivateSubscriber', { domain: '.ascd.org' });
  deleteCookie(WITSBY_USER_EMAIL_COOKIE_NAME);
  localStorage.clear();
  window.location.href = `${process.env.SAML_URL}/auth/logout`;
};

const getNewRefreshToken = async () => {
  try {
    const refreshToken = getCookie('refreshToken');
    const response = await fetch(`${process.env.SAML_URL}/newToken`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    });
    const data = await response.json();
    const newAccessToken = data?.accessToken;
    const newRefreshToken = data?.refreshToken;
    if (newAccessToken && newRefreshToken) {
      setCookie('accessToken', newAccessToken);
      setCookie('refreshToken', newRefreshToken);
      return { accessToken: newAccessToken, refreshToken: newRefreshToken };
    }
    return { accessToken: '', refreshToken: '' };
  } catch (error) {
    return { accessToken: '', refreshToken: '' };
  }
};

const processQueue = (error: Error | null, token: string | null = null) => {
  failedRequestsQueue.forEach((request) => {
    if (error) {
      request.reject(error);
    } else {
      request.resolve(request.forward(request.operation));
    }
  });
  failedRequestsQueue.length = 0;
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    if (graphQLErrors?.some((v) => v?.extensions?.code === 'UNAUTHENTICATED')) {
      return new Observable<FetchResult<Record<string, unknown>>>((observer) => {
        (async () => {
          try {
            const oktaAccessToken = get(
              localStorage.getItem('okta-token-storage')
                ? JSON.parse(localStorage.getItem('okta-token-storage') || '')
                : '',
              'accessToken.accessToken',
            );

            if (!oktaAccessToken) {
              if (!isRefreshing) {
                isRefreshing = true;
                tokenRefreshPromise = getNewRefreshToken();

                try {
                  const tokens = await tokenRefreshPromise;
                  if (!tokens?.accessToken || !tokens?.refreshToken) {
                    processQueue(new Error('Failed to refresh token'));
                    logOut();
                    return;
                  }
                  processQueue(null, tokens.accessToken);
                } catch (error) {
                  processQueue(error as Error);
                  logOut();
                  return;
                } finally {
                  isRefreshing = false;
                  tokenRefreshPromise = null;
                }
              }

              // If a refresh is already in progress, wait for it
              if (tokenRefreshPromise) {
                return new Promise((resolve, reject) => {
                  failedRequestsQueue.push({
                    resolve,
                    reject,
                    operation,
                    forward,
                  });
                })
                  .then((value) => {
                    if (value instanceof Observable) {
                      value.subscribe(observer);
                    }
                  })
                  .catch((error) => observer.error(error));
              }
            }

            // Retry the failed request
            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            };

            forward(operation).subscribe(subscriber);
          } catch (error) {
            observer.error(error);
          }
        })();
      });
    }
    const def = getMainDefinition(operation.query);
    if (def && def?.operation === 'query') {
      // handleGraphqlError(errors);
    }
    graphQLErrors.map((graphqlError) => graphqlError);
  }
  if (networkError) {
    console.error('networkError', networkError);
  }
  return forward(operation);
  // prepared for logging
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {},
    },
    // Disable normalization for AuthorInfo type to prevent cache pollution
    // when the same user appears as author in different org contexts
    AuthorInfo: {
      keyFields: false,
    },
    // Disable normalization for OrganizationInfo to prevent org data
    // from being merged across different query contexts
    OrganizationInfo: {
      keyFields: false,
    },
  },
});

const contentfulCache = new InMemoryCache({});

const createApolloClient = () => {
  const authLink = setContext((_, { headers }) => {
    const oktaAccessToken = get(
      localStorage.getItem('okta-token-storage')
        ? JSON.parse(localStorage.getItem('okta-token-storage') || '')
        : '',
      'accessToken.accessToken',
    );
    const accessToken = getCookie('accessToken');
    const userinfo = localStorage.getItem('header-info') || '';
    return {
      headers: {
        ...headers,
        userinfo,
        authorization:
          accessToken || oktaAccessToken ? `Bearer ${accessToken || oktaAccessToken}` : '',
      },
    };
  });

  const uri = process.env.SERVER_URL;

  const httpLink = new HttpLink({
    uri,
    credentials: 'same-origin',
  });

  const cleanTypenameLink = new ApolloLink((operation, forward) => {
    const def = getMainDefinition(operation.query);
    if (def && def.operation === 'mutation') {
      // eslint-disable-next-line no-param-reassign
      operation.variables = omitDeep(operation.variables, ['__typename']);
    }
    return forward ? forward(operation) : null;
  });

  return new ApolloClient({
    cache,
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([cleanTypenameLink, errorLink, authLink.concat(httpLink)]),
  });
};

export function initializeApollo(
  initialState: NormalizedCacheObject | null,
): ApolloClient<NormalizedCacheObject> {
  const apolloClientInitialized = apolloClient ?? createApolloClient();

  if (initialState) {
    const existingCache = apolloClientInitialized.extract();

    apolloClientInitialized.cache.restore({
      ...existingCache,
      ...initialState,
    });
  }

  if (typeof window === 'undefined') return apolloClientInitialized;

  if (!apolloClient) apolloClient = apolloClientInitialized;

  return apolloClientInitialized;
}

export function useApollo(
  initialState: NormalizedCacheObject | null,
): ApolloClient<NormalizedCacheObject> {
  const store = useMemo(() => initializeApollo(initialState), [initialState]);
  return store;
}

export const contentfulDirectClient = new ApolloClient({
  connectToDevTools: true,
  link: ApolloLink.from([
    onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.map(({ message, locations, path }) =>
          console.log(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          ),
        );
      }
      if (networkError) console.error(`[Network error]: ${networkError}`);
    }),
    new HttpLink({
      uri: `https://graphql.contentful.com/content/v1/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID}/environments/${process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT}`,
      credentials: 'same-origin',
      headers: {
        Authorization: `Bearer ${
          process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW
            ? process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN
            : process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN
        }`,
      },
    }),
  ]),
  cache: contentfulCache,
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'ignore',
    },
  },
});
